import React from 'react';
import styled from 'styled-components';
import { TextField } from '@material-ui/core';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { FormikConfig, useFormik } from 'formik';
import * as yup from 'yup';
import { ContentfulGlobalMicrocopy } from '../../graphql-types';
import Button from './Button';
import Typography from './Typography';
import { getPlainTextFromMicrocopy } from '../utils/getPlainTextFromMicrocopy';
import { submitEmailSubscription } from '../services/email-subscription.service';
import { Alert, AlertTitle } from '@material-ui/lab';
import {
  trackFormStart,
  trackFormSubmit,
  trackFormError,
} from '../lib/analytics/formTracking';

const StyledTextField = styled(TextField)`
  & .MuiOutlinedInput-root {
    border-radius: 0px;
    background-color: ${(props) => props.theme.color.white};

    & fieldset {
      border-color: ${(props) => props.theme.color.primary};
    }
  }
  & .MuiFormHelperText-root {
    position: absolute;
    bottom: 0;
    transform: translateY(100%);
    white-space: nowrap;
  }
`;

enum FORM_STATE {
  INITIAL,
  SUCCESS,
  ERROR,
}

interface SignUpProps {
  className?: string;
  title?: string;
  variant?: string;
  formFieldLabels?: ContentfulGlobalMicrocopy[];
}
interface SignUpLabels {
  SignUpButtonLabel?: string;
  SignUpEmail?: string;
  SignUpEmailValidation?: string;
  SignUpEmailRequired?: string;
  SignUpSuccess?: string;
  SignUpError?: string;
}
const SignUpSubscription: React.FC<SignUpProps> = (props) => {
  const { className, title, formFieldLabels } = props;

  // setup form labels, validation, and formik options
  const [formState, setFormState] = React.useState(FORM_STATE.INITIAL);
  const labels: SignUpLabels = {};
  formFieldLabels?.forEach((formField) => {
    labels[formField.key] = getPlainTextFromMicrocopy(formField.copy?.raw);
  });

  // Form tracking constants
  const FORM_ID = 'newsletter-signup';
  const PAGE_SECTION = 'footer';
  const formStartTracked = React.useRef(false);

  // Handler for form_start event (fires once on first focus)
  const handleFormFocus = () => {
    if (!formStartTracked.current) {
      formStartTracked.current = true;
      trackFormStart(FORM_ID, PAGE_SECTION);
    }
  };

  const signUpFormSchema = yup.object().shape({
    email: yup
      .string()
      .email(
        labels.SignUpEmailValidation || 'Please enter a valid email address',
      )
      .required(labels.SignUpEmailRequired || 'This field is mandatory'),
    recaptchaToken: yup.string().required(),
  });
  const signUpFormikOptions: FormikConfig<{
    email: string;
    recaptchaToken: string;
  }> = {
    initialValues: {
      email: '',
      recaptchaToken: '',
    },
    validationSchema: signUpFormSchema,
    onSubmit: async (values, { resetForm }) => {
      if (!values.recaptchaToken) {
        handleReCaptchaVerify();
        return;
      }

      submitEmailSubscription(values.email, values.recaptchaToken)
        .then(() => {
          trackFormSubmit(FORM_ID, 'ajax', 'success');
          setFormState(FORM_STATE.SUCCESS);

          // unset success message afterwards
          setTimeout(() => {
            setFormState(FORM_STATE.INITIAL);
          }, 5000);

          // reset form
          resetForm();
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Could not submit form', error);
          trackFormSubmit(FORM_ID, 'ajax', 'failure');
          trackFormError(FORM_ID, 'form', 'server_error');
          setFormState(FORM_STATE.ERROR);
        })
        .finally(() => {
          formik.setSubmitting(false);
        });
    },
  };
  const formik = useFormik(signUpFormikOptions);

  // Track validation errors when form submission is attempted
  const lastSubmitCount = React.useRef(0);
  React.useEffect(() => {
    // Only track errors when submitCount increases (user clicked submit)
    if (formik.submitCount > lastSubmitCount.current) {
      lastSubmitCount.current = formik.submitCount;

      // Track each validation error (excluding recaptchaToken which is internal)
      const errorFields = Object.keys(formik.errors).filter(
        (field) => field !== 'recaptchaToken',
      );

      errorFields.forEach((fieldName) => {
        // For email field, determine if it's a format or required error
        const errorType =
          fieldName === 'email' && formik.values.email
            ? 'invalid_format'
            : 'required';
        trackFormError(FORM_ID, fieldName, errorType);
      });
    }
  }, [formik.submitCount, formik.errors, formik.values.email]);

  // reCAPTCHAv3 state and functions
  const { executeRecaptcha } = useGoogleReCaptcha();
  const handleReCaptchaVerify = React.useCallback(async () => {
    if (!executeRecaptcha) {
      // eslint-disable-next-line no-console
      console.error('Execute recaptcha not yet available');
      return;
    }

    const token = await executeRecaptcha('sign_up');
    formik.setFieldValue('recaptchaToken', token);

    // N.B. reCAPTCHA tokens expire after 2 minutes, so we only
    // fire this function when users click on the email form
  }, [executeRecaptcha]);

  return (
    <div className="">
      {title && (
        <Typography
          as="div"
          variant="callout"
          className={`${className || 'text-center md:text-left'}`}
        >
          {title}
        </Typography>
      )}
      {formik && (
        <form
          onSubmit={formik.handleSubmit}
          onFocus={handleFormFocus}
          noValidate
        >
          <div className="flex flex-wrap gap-y-s2 items-bottom mt-s2">
            <StyledTextField
              id="email"
              name="email"
              type="email"
              label={labels.SignUpEmail || '* Email'}
              variant="outlined"
              placeholder="yourname@email.com"
              className="flex-1 min-w-[150px]"
              value={formik.values.email}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              onChange={formik.handleChange}
              onClick={handleReCaptchaVerify}
            />

            <Button
              type="submit"
              variant={`primary`}
              className="flex-none min-w-[150px]"
            >
              {labels.SignUpButtonLabel || `Sign Up`}
            </Button>
          </div>
          {formState === FORM_STATE.SUCCESS && (
            <Alert severity="success" className="mt-s2">
              <AlertTitle>
                {labels.SignUpSuccess ||
                  'Thank you! Your subscription was successful.'}
              </AlertTitle>
            </Alert>
          )}
          {formState === FORM_STATE.ERROR && (
            <Alert severity="error" className="mt-s2">
              <AlertTitle>
                {labels.SignUpError ||
                  'Sorry! An error occurred while submitting.'}
              </AlertTitle>
            </Alert>
          )}
        </form>
      )}
    </div>
  );
};

export default SignUpSubscription;
